import { Link } from "react-router-dom";
import { YodiMark } from "@/components/brand/YodiMark";
import { LanguageSelector } from "@/components/ui/language-selector";
import { useApp } from "@/hooks/use-app-context";
import { isCollector } from "./navigation-registry";
import { Icon } from "@/lib/icons";
import { contributorHeaderCopy } from "./contributor-header-copy";
import { ContributorTabs } from "./ContributorTabs";
import { contributorTabs } from "./contributor-tabs";
import { contributorTasks } from "@/features/contributor-tasks/contributor-tasks";
import { HeaderThemeToggle } from "./HeaderThemeToggle";
import { UserMenu } from "./UserMenu";
import { OfflineStatusButton } from "@/features/offline/components/OfflineStatusButton";

interface ContributorHeaderProps {
  lang: string;
  /** Available earnings, when the profile has a payout situation. */
  balance: number | null;
}

/**
 * Header of the signed-in contributor app: the logo home, the tasks as verbs,
 * then website, balance, language, theme and the account menu.
 */
export function ContributorHeader({ lang, balance }: ContributorHeaderProps) {
  const { user } = useApp();
  const copy = contributorHeaderCopy[lang === "en" ? "en" : "fr"];
  const base = `/${lang}`;
  const collector = isCollector(user);
  const amount = balance === null ? null : `${new Intl.NumberFormat(lang === "en" ? "en-GB" : "fr-FR").format(balance)}\u00a0F`;
  const tabs = contributorTabs(lang, copy, contributorTasks(lang, { collector }), collector, amount);

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/90 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
        <Link to={base} className="flex shrink-0 items-center gap-2.5" aria-label="Yodi Data Hub">
          <YodiMark className="size-9 rounded-lg" />
          <span className="hidden text-base font-semibold text-foreground sm:inline">Yodi</span>
        </Link>

        <div className="flex flex-1 justify-center">
          <ContributorTabs tabs={tabs} label={copy.nav} />
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <Link
            to={`${base}/site`}
            className="hidden h-10 items-center gap-1.5 rounded-full border border-border/60 px-3.5 text-sm font-medium text-foreground transition-colors hover:bg-surface-2 xl:inline-flex"
          >
            <Icon name="externalLink" className="size-4" />
            {copy.website}
          </Link>
          {/* Phones only: on large screens the amount sits in the Gains entry. */}
          {amount ? (
            <Link
              to={`${base}/get-paid?section=overview`}
              aria-label={copy.balance.replace("{amount}", amount)}
              className="inline-flex h-10 lg:hidden items-center gap-1.5 rounded-full bg-primary px-3.5 text-sm font-semibold tabular-nums text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <Icon name="wallet" className="size-4" />
              {amount}
            </Link>
          ) : null}
          <OfflineStatusButton />
          <LanguageSelector variant="header" className="hidden md:inline-flex" />
          <HeaderThemeToggle />
          <UserMenu lang={lang} />
        </div>
      </div>
    </header>
  );
}
